import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { navLinks, doctor } from "../lib/data";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-nav" : "bg-transparent"
      }`}
    >
      <div className="container-luxe flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-baseline gap-2 group"
        >
          <span className="font-serif text-2xl md:text-[28px] tracking-tight leading-none">
            Tetiana Zolotko
          </span>
          <span className="hidden sm:inline overline text-warmGray">Clinic</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navLinks.slice(1).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-[13px] tracking-wide transition-colors ${
                  isActive
                    ? "text-ink"
                    : "text-warmGray hover:text-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/booking"
            data-testid="nav-book-cta"
            className="hidden md:inline-flex items-center rounded-full bg-ink text-bone px-6 py-3 text-[11px] tracking-[0.25em] uppercase hover:bg-black transition"
          >
            Онлайн запис
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 -mr-2"
            aria-label="Меню"
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden glass-nav border-t border-black/5"
            data-testid="nav-mobile-panel"
          >
            <div className="container-luxe py-6 flex flex-col gap-1">
              {navLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.to.replace("/", "") || "home"}`}
                  className={({ isActive }) =>
                    `py-3 text-base border-b border-black/5 ${
                      isActive ? "text-ink" : "text-warmGray"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/booking"
                data-testid="nav-mobile-book-cta"
                className="mt-4 inline-flex justify-center items-center rounded-full bg-ink text-bone px-6 py-4 text-[11px] tracking-[0.25em] uppercase"
              >
                Онлайн запис
              </Link>
              <div className="mt-4 flex flex-col gap-1 text-sm text-warmGray">
                <a href={`tel:${doctor.phonesRaw[0]}`}>{doctor.phones[0]}</a>
                <a href={doctor.telegram} target="_blank" rel="noreferrer">
                  Telegram {doctor.telegramHandle}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
